import React from 'react';
import { FormattingToolbar } from '../ui/FormattingToolbar';

export function RichTextSections({
  showIntroField, setShowIntroField, introTextRef, isTypingIntro, debouncedIntroUpdate, introTypingTimeout, setIntroText, applyIntroFormat,
  showOutroField, setShowOutroField, outroTextRef, isTypingOutro, debouncedOutroUpdate, outroTypingTimeout, setOutroText, applyOutroFormat,
  showCancelField, setShowCancelField, cancelTextRef, isTypingCancel, debouncedCancelUpdate, cancelTypingTimeout, setCancelText, applyCancelFormat,
  cancelOutroTextRef, isTypingCancelOutro, debouncedCancelOutroUpdate, cancelOutroTypingTimeout, setCancelOutroText, applyCancelOutroFormat,
}) {
  return (
    <>
          {/* Intro Text Toggle */}
          <div className="mb-6 sm:mb-8">
            <button
              onClick={() => setShowIntroField(!showIntroField)}
              className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"
            >
              <svg className={`w-4 h-4 transition-transform ${showIntroField ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Untertitel unter dem Fragebogen-Titel bearbeiten (optional)
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">Dieser Text wird den Bewerbern unter dem Fragebogen-Titel angezeigt.</p>

            {showIntroField && (
              <div className="mt-3 ml-6">
                <FormattingToolbar onFormat={(tag) => applyIntroFormat(tag)} />
                <div
                  ref={introTextRef}
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    isTypingIntro.current = true;
                    debouncedIntroUpdate(e.target.innerHTML);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    document.execCommand('insertText', false, text);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    if (introTypingTimeout.current) {
                      clearTimeout(introTypingTimeout.current);
                    }
                    setIntroText(e.target.innerHTML);
                    isTypingIntro.current = false;
                  }}
                  className="content-editable-field w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 text-base min-h-[60px] empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
                  data-placeholder="Bitte füllen Sie die folgenden Felder aus."
                />
              </div>
            )}
          </div>

          {/* Outro Text Toggle */}
          <div className="mb-6 sm:mb-8">
            <button
              onClick={() => setShowOutroField(!showOutroField)}
              className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"
            >
              <svg className={`w-4 h-4 transition-transform ${showOutroField ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Abschlusstext bearbeiten (optional)
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">Dieser Text wird den Bewerbern nach dem Absenden angezeigt ("Die nächsten Schritte").</p>

            {showOutroField && (
              <div className="mt-3 ml-6">
                <FormattingToolbar onFormat={(tag) => applyOutroFormat(tag)} />
                <div
                  ref={outroTextRef}
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    isTypingOutro.current = true;
                    debouncedOutroUpdate(e.target.innerHTML);
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text/plain');
                    document.execCommand('insertText', false, text);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDragStart={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    if (outroTypingTimeout.current) {
                      clearTimeout(outroTypingTimeout.current);
                    }
                    setOutroText(e.target.innerHTML);
                    isTypingOutro.current = false;
                  }}
                  className="content-editable-field w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 text-base min-h-[120px] empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
                  data-placeholder="Vielen Dank für Ihre Zeit und die sorgfältigen Antworten..."
                />
              </div>
            )}
          </div>

          {/* Cancel Text Toggle */}
          <div className="mb-6 sm:mb-8">
            <button
              onClick={() => setShowCancelField(!showCancelField)}
              className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"
            >
              <svg className={`w-4 h-4 transition-transform ${showCancelField ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Absagetexte bearbeiten (optional)
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 ml-6">Diese Texte werden angezeigt, wenn Bewerber ihre Bewerbung zurückziehen möchten.</p>

            {showCancelField && (
              <div className="mt-3 ml-6 space-y-6">
                {/* Cancel Dialog Text */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Absagedialog-Text
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Dieser Text wird im Absagedialog angezeigt.</p>
                  <FormattingToolbar onFormat={(tag) => applyCancelFormat(tag)} />
                  <div
                    ref={cancelTextRef}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onInput={(e) => {
                      isTypingCancel.current = true;
                      debouncedCancelUpdate(e.target.innerHTML);
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = e.clipboardData.getData('text/plain');
                      document.execCommand('insertText', false, text);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      if (cancelTypingTimeout.current) {
                        clearTimeout(cancelTypingTimeout.current);
                      }
                      setCancelText(e.target.innerHTML);
                      isTypingCancel.current = false;
                    }}
                    className="content-editable-field w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 text-base min-h-[100px] empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
                    data-placeholder="Du hast gemerkt, dass wir vielleicht nicht die richtige Passung sind? Kein Problem — das ist für beide Seiten wichtig! Hier kannst du uns einfach mitteilen, dass du deine Bewerbung zurückziehen möchtest."
                  />
                </div>

                {/* Cancel Confirmation Text */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Absagebestätigung-Text
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Dieser Text wird nach der Absagebestätigung angezeigt.</p>
                  <FormattingToolbar onFormat={(tag) => applyCancelOutroFormat(tag)} />
                  <div
                    ref={cancelOutroTextRef}
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onInput={(e) => {
                      isTypingCancelOutro.current = true;
                      debouncedCancelOutroUpdate(e.target.innerHTML);
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = e.clipboardData.getData('text/plain');
                      document.execCommand('insertText', false, text);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                      if (cancelOutroTypingTimeout.current) {
                        clearTimeout(cancelOutroTypingTimeout.current);
                      }
                      setCancelOutroText(e.target.innerHTML);
                      isTypingCancelOutro.current = false;
                    }}
                    className="content-editable-field w-full p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 text-base min-h-[100px] empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:pointer-events-none"
                    data-placeholder="Vielen Dank für deine Zeit! Du hast deine Bewerbung erfolgreich abgesagt. Falls du dich doch nochmal entscheidest, kannst du gerne eine neue Bewerbung einreichen."
                  />
                </div>
              </div>
            )}
          </div>
    </>
  );
}
